<script setup lang="ts">
  import { computed } from 'vue'
  import { ChevronLeft, ChevronRight, Link, Repeat } from 'lucide-vue-next'
  import type {
    SuperUserQuestionnaire as Questionnaire,
    QuestionnaireSection,
    QuestionnaireQuestion
  } from '@/types/generated'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import { useTranslation } from '@/composables/useTranslation'
  import UserQuestionnaireQuestion from '@/components/app/questionnaire/editor/UserQuestionnaireQuestion.vue'
  import { Switch } from '@/components/ui/switch'
  import UnlinkFieldIndicator from '@/components/icons/UnlinkFieldIndicator.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import VirtualQuestionContent from './VirtualQuestionContent.vue'
  const { t, locale } = useTranslation({
    en: {
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      question: 'Question',
      required: 'Required',
      connectedFieldTooltip: 'Connected to case data field',
      unconnectedFieldTooltip: 'Not connected to any case data field',
      virtualQuestionTitle: 'Would you like to add another {participantType}?',
      autoGeneratedTooltip:
        'This question was created automatically. If you want to remove it, turn off the "Repeatable" option.'
    },
    de: {
      previousQuestion: 'Vorherige Frage',
      nextQuestion: 'Nächste Frage',
      question: 'Frage',
      required: 'Erforderlich',
      connectedFieldTooltip: 'Mit Falldatenfeld verbunden',
      unconnectedFieldTooltip: 'Nicht mit einem Falldatenfeld verbunden',
      virtualQuestionTitle: 'Möchten Sie einen weiteren {participantType} hinzufügen?',
      autoGeneratedTooltip:
        'Diese Frage wurde automatisch erstellt. Wenn Sie sie entfernen möchten, deaktivieren Sie die Option "Wiederholbar".'
    }
  })

  const props = defineProps<{
    questionnaire: Questionnaire
    questionUuid: string
  }>()

  const questionnaireEditorStore = useQuestionnaireEditorStore()

  // Helper function to get participant name for a section
  const getParticipantName = (section: QuestionnaireSection): string | null => {
    if ((section as any).type !== 'participant') return null
    const participantType = (section as any).participant_type
    if (!participantType) return null

    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN
    return entityTranslations[participantType as keyof typeof entityTranslations] || participantType
  }

  const currentQuestionContext = computed(() => {
    let currentSection: QuestionnaireSection | null = null
    let currentQuestion: QuestionnaireQuestion | null = null
    let questionIndexInSection = 0
    let totalQuestionsInSection = 0
    let isVirtual = false

    // Check if this is a virtual question
    if (props.questionUuid.startsWith('virtual-question-')) {
      const sectionUuid = props.questionUuid.replace('virtual-question-', '')
      currentSection = props.questionnaire.schema?.find((s) => s.uuid === sectionUuid) || null

      if (currentSection) {
        isVirtual = true
        questionIndexInSection = (currentSection.items?.length || 0) + 1 // Virtual question is always last
        totalQuestionsInSection =
          (currentSection.items?.length || 0) + (hasVirtualQuestion(currentSection) ? 1 : 0)
      }
    } else {
      // Find the current question and calculate its position within the section
      for (const section of props.questionnaire.schema || []) {
        for (let i = 0; i < (section.items || []).length; i++) {
          const question = section.items![i]
          if (question.uuid === props.questionUuid) {
            currentSection = section
            currentQuestion = question
            questionIndexInSection = i + 1 // 1-based index
            totalQuestionsInSection =
              (section.items?.length || 0) + (hasVirtualQuestion(section) ? 1 : 0)
            break
          }
        }
        if (currentQuestion) break
      }
    }

    return {
      question: currentQuestion,
      section: currentSection,
      questionNumber: questionIndexInSection,
      totalQuestions: totalQuestionsInSection,
      sectionTitle: currentSection?.title || 'Untitled Section',
      isVirtual
    }
  })

  // Helper to check if section has virtual question
  const hasVirtualQuestion = (section: QuestionnaireSection): boolean => {
    return (section as any).repeatable === true && (section as any).participant_type != null
  }

  // Get the virtual question title
  const virtualQuestionTitle = computed<string>(() => {
    if (!currentQuestionContext.value.isVirtual || !currentQuestionContext.value.section) {
      return 'Would you like to add another participant?'
    }
    const participantName = getParticipantName(currentQuestionContext.value.section)
    if (!participantName) return 'Would you like to add another participant?'
    return t('virtualQuestionTitle', { participantType: participantName })
  })

  // Get all questions in order for navigation (including virtual questions)
  const allQuestions = computed(() => {
    const questions: {
      question: QuestionnaireQuestion | null
      section: QuestionnaireSection
      isVirtual: boolean
      uuid: string
    }[] = []

    for (const section of props.questionnaire.schema || []) {
      // Add regular questions
      for (const question of section.items || []) {
        questions.push({
          question,
          section,
          isVirtual: false,
          uuid: question.uuid
        })
      }

      // Add virtual question if section is repeatable with participant type
      if (hasVirtualQuestion(section)) {
        questions.push({
          question: null,
          section,
          isVirtual: true,
          uuid: `virtual-question-${section.uuid}`
        })
      }
    }

    return questions
  })

  // Find current question index in the flat list
  const currentQuestionIndex = computed(() => {
    return allQuestions.value.findIndex((item) => item.uuid === props.questionUuid)
  })

  // Navigation functions
  const goToPreviousQuestion = () => {
    const prevIndex = currentQuestionIndex.value - 1
    if (prevIndex >= 0) {
      const prevItem = allQuestions.value[prevIndex]
      if (prevItem.isVirtual) {
        questionnaireEditorStore.selectVirtualQuestion(prevItem.section.uuid)
      } else {
        questionnaireEditorStore.selectQuestion(prevItem.question!.uuid)
      }
    }
  }

  const goToNextQuestion = () => {
    const nextIndex = currentQuestionIndex.value + 1
    if (nextIndex < allQuestions.value.length) {
      const nextItem = allQuestions.value[nextIndex]
      if (nextItem.isVirtual) {
        questionnaireEditorStore.selectVirtualQuestion(nextItem.section.uuid)
      } else {
        questionnaireEditorStore.selectQuestion(nextItem.question!.uuid)
      }
    }
  }

  // Check if navigation is available
  const hasPreviousQuestion = computed(() => currentQuestionIndex.value > 0)
  const hasNextQuestion = computed(() => currentQuestionIndex.value < allQuestions.value.length - 1)
</script>

<template>
  <div
    v-if="currentQuestionContext.question && currentQuestionContext.section"
    class="flex h-full w-full flex-col"
  >
    <div
      class="inline-flex items-center justify-between self-stretch rounded-xl bg-white p-4
        shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
    >
      <div class="inline-flex flex-1 flex-col items-start justify-start gap-2.5">
        <button
          :disabled="!hasPreviousQuestion"
          class="inline-flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          @click="goToPreviousQuestion"
        >
          <div class="relative h-5 w-5 overflow-hidden">
            <ChevronLeft class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
          </div>
          <div class="flex items-center justify-center gap-1.5 px-1.5">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('previousQuestion') }}
            </div>
          </div>
        </button>
      </div>

      <div class="inline-flex flex-1 flex-col items-center justify-start">
        <div
          class="justify-start font-['Inter'] text-base font-medium leading-normal text-gray-800"
        >
          {{ t('question') }} {{ currentQuestionContext.questionNumber }}/{{
            currentQuestionContext.totalQuestions
          }}
        </div>
        <div class="justify-start font-['Inter'] text-xs font-normal leading-none text-gray-600">
          {{ currentQuestionContext.sectionTitle }}
        </div>
      </div>

      <div class="flex flex-1 items-center justify-end gap-2">
        <button
          :disabled="!hasNextQuestion"
          class="flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          @click="goToNextQuestion"
        >
          <div class="flex items-center justify-center gap-1.5 px-1.5">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('nextQuestion') }}
            </div>
          </div>
          <div class="relative h-5 w-5 overflow-hidden">
            <ChevronRight class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
          </div>
        </button>
      </div>
    </div>

    <div
      class="mt-2 flex w-full flex-col items-start justify-start self-stretch overflow-hidden rounded-xl bg-white
        shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
    >
      <div
        class="inline-flex h-16 items-center justify-start gap-2 self-stretch border-b border-gray-200 bg-white p-4"
      >
        <!-- Icon Indicator (Link/Unlink for regular, Repeat for virtual) -->
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <div class="flex items-center justify-center">
                <Repeat
                  v-if="currentQuestionContext.isVirtual"
                  class="h-5 w-5 text-gray-400"
                />
                <Link
                  v-else-if="
                    currentQuestionContext.question!.tag &&
                    currentQuestionContext.question!.tag !== ''
                  "
                  class="h-5 w-5 text-emerald-600"
                />
                <UnlinkFieldIndicator v-else />
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              variant="secondary"
              :show-arrow="true"
            >
              <template v-if="currentQuestionContext.isVirtual">
                {{ t('autoGeneratedTooltip') }}
              </template>
              <template v-else>
                {{
                  currentQuestionContext.question!.tag &&
                  currentQuestionContext.question!.tag !== ''
                    ? t('connectedFieldTooltip')
                    : t('unconnectedFieldTooltip')
                }}
              </template>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div class="flex flex-1 items-center justify-start gap-2">
          <!-- Virtual question title (read-only) -->
          <span
            v-if="currentQuestionContext.isVirtual && currentQuestionContext.section"
            class="flex-1 font-['Inter'] text-lg font-semibold leading-7 text-gray-800"
          >
            {{
              t('virtualQuestionTitle', {
                participantType: getParticipantName(currentQuestionContext.section)
              })
            }}
          </span>
          <!-- Regular question title (editable) -->
          <input
            v-else
            v-model="currentQuestionContext.question!.label"
            type="text"
            placeholder="Untitled Question"
            class="flex-1 cursor-text border-0 bg-transparent p-0 font-['Inter'] text-lg font-semibold leading-7
              text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
        </div>

        <div class="flex items-center justify-start gap-4">
          <div class="flex items-center justify-start gap-2">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('required') }}
            </div>
            <Switch
              v-if="currentQuestionContext.isVirtual"
              :checked="true"
              disabled
            />
            <Switch
              v-else
              :checked="!currentQuestionContext.question!.optional"
              @update:checked="(checked) => (currentQuestionContext.question!.optional = !checked)"
            />
          </div>
        </div>
      </div>
      <div class="flex w-full flex-col items-start justify-start gap-4 self-stretch p-4">
        <!-- Virtual Question Content -->
        <VirtualQuestionContent
          v-if="currentQuestionContext.isVirtual"
          :question-title="virtualQuestionTitle"
        />

        <!-- Regular Question Content -->
        <UserQuestionnaireQuestion
          v-else
          :uuid="currentQuestionContext.question.uuid"
          :section-uuid="currentQuestionContext.section.uuid"
          :questionnaire="questionnaire"
          :show-header="false"
        />
      </div>
    </div>
  </div>
</template>
